import { AfterViewChecked, Component, ElementRef, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { DsColors } from '@levelaccess/design-system';
import { IScreenHighlight } from '../../interfaces/screen-highlight.interface';
import { SharedCommonUtility } from '../../../../shared/utils/common.utility';
import { INVALID_SCREEN_HIGHLIGHT } from '../../constants/screen-highlight.constants';

@Component({
  selector: 'app-screen-highlights',
  templateUrl: './screen-highlights.component.html',
  styleUrls: ['./screen-highlights.component.scss'],
  standalone: false,
})
export class ScreenHighlightsComponent implements OnChanges, AfterViewChecked {
  private readonly HIGHLIGHT_COLOR: DsColors = DsColors.Focus;
  private readonly HIGHLIGHT_WIDTH_PX: number = 3;
  private readonly LABEL_BOX_WIDTH_PX: number = 24;
  private readonly LABEL_BOX_HEIGHT_PX: number = 24;
  private readonly FONT: string = 'bold 12px Arial';
  private readonly TEXT_COLOR: string = 'white';
  private readonly TEXT_ALIGN: CanvasTextAlign = 'center';
  private readonly TEXT_BASELINE: CanvasTextBaseline = 'middle';

  private _highlights: IScreenHighlight[];
  private needsRender: boolean = false;

  @Input({ required: true })
  public width: number;

  @Input({ required: true })
  public height: number;

  @Input({ required: false })
  public altText: string = '';

  @Input({ required: true })
  public highlights: IScreenHighlight[];

  @Input({ required: false })
  public scale: number;

  @Input({ required: false })
  public hideLabels: boolean = false;

  @ViewChild('canvas')
  public canvas: ElementRef<HTMLCanvasElement>;

  ngOnChanges(changes: SimpleChanges): void {
    const highlightsChanged: boolean = changes.highlights?.currentValue !== changes.highlights?.previousValue;
    const scaleChanged: boolean = changes.scale?.currentValue !== changes.scale?.previousValue;
    const widthChanged: boolean = changes.width?.currentValue !== changes.width?.previousValue;
    const heightChanged: boolean = changes.height?.currentValue !== changes.height?.previousValue;

    if (highlightsChanged) {
      const highlights: IScreenHighlight[] = changes.highlights?.currentValue || [];
      this._highlights = highlights.filter((highlight: IScreenHighlight) => highlight !== INVALID_SCREEN_HIGHLIGHT);
    }

    if (highlightsChanged || scaleChanged || widthChanged || heightChanged) {
      // Defer to ngAfterViewChecked: Angular applies this component's own [width]/[height] canvas
      // bindings after ngOnChanges runs, and setting those attributes always resets the canvas bitmap,
      // which would wipe out anything drawn here before that reset happens.
      this.needsRender = true;
    }
  }

  ngAfterViewChecked(): void {
    if (this.needsRender) {
      this.needsRender = false;
      this.renderHighlights();
    }
  }

  private renderHighlights(): void {
    if (SharedCommonUtility.isNullish(this.canvas)) {
      return;
    }

    this.clearHighlights();

    if (SharedCommonUtility.isNullishOrEmpty(this._highlights)) {
      return;
    }

    const scale: number = SharedCommonUtility.notNullish(this.scale) && isFinite(this.scale) ? this.scale : 1;
    const element: HTMLCanvasElement = this.canvas.nativeElement;
    const ctx: CanvasRenderingContext2D = this.canvas.nativeElement.getContext('2d');

    ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
    ctx.fillRect(0, 0, element.width, element.height);

    const getCoords: (highlight: IScreenHighlight) => { x0: number; y0: number; x1: number; y1: number } = (
      highlight: IScreenHighlight,
    ): { x0: number; y0: number; x1: number; y1: number } => {
      return {
        x0: highlight.x0 / scale,
        y0: highlight.y0 / scale,
        x1: highlight.x1 / scale,
        y1: highlight.y1 / scale,
      };
    };

    this._highlights.forEach((highlight: IScreenHighlight): void => {
      const { x0, y0, x1, y1 }: { x0: number; y0: number; x1: number; y1: number } = getCoords(highlight);

      // Clear the highlight area
      ctx.clearRect(x0, y0, x1 - x0, y1 - y0);

      // Draw the outline around the highlight area
      ctx.strokeStyle = this.HIGHLIGHT_COLOR;
      ctx.lineWidth = this.HIGHLIGHT_WIDTH_PX;
      ctx.strokeRect(x0, y0, x1 - x0, y1 - y0);
    });

    if (this.hideLabels) {
      return;
    }

    this._highlights.forEach((highlight: IScreenHighlight): void => {
      const { x0, y0 }: { x0: number; y0: number } = getCoords(highlight);

      // Draw the label box
      const labelBoxHorizontalOffset: number = this.HIGHLIGHT_WIDTH_PX / 2;
      ctx.fillStyle = this.HIGHLIGHT_COLOR;
      ctx.fillRect(
        x0 - labelBoxHorizontalOffset,
        y0 - this.LABEL_BOX_HEIGHT_PX,
        this.LABEL_BOX_WIDTH_PX,
        this.LABEL_BOX_HEIGHT_PX,
      );

      // Add the label text
      ctx.font = this.FONT;
      ctx.fillStyle = this.TEXT_COLOR;
      ctx.textAlign = this.TEXT_ALIGN;
      ctx.textBaseline = this.TEXT_BASELINE;
      ctx.fillText(
        highlight.label,
        x0 + this.LABEL_BOX_WIDTH_PX / 2 - labelBoxHorizontalOffset,
        y0 - this.LABEL_BOX_HEIGHT_PX / 2,
      );
    });
  }

  private clearHighlights(): void {
    const context: CanvasRenderingContext2D = this.canvas.nativeElement.getContext('2d');
    context.clearRect(0, 0, context.canvas.width, context.canvas.height);
  }
}
